import React, { useEffect, useRef, useState } from 'react';
import { mod } from './index';

// Tug of War — 10s rapid-tap. Score = taps × strength multiplier.
// Visual: a rope marker that pulls toward your side as you out-tap an
// average opponent baseline. Higher STR makes each of your taps count more.

const GAME_DURATION_MS = 10000;
// What an "average" hand looks like — used as the visual baseline pull rate
// against the rope marker so single-player play has feedback. Final score
// is just "taps × multiplier"; the visual is purely flavour.
const AVG_TAP_RATE_HZ  = 6;

function multiplierForMod(myMod, theirMod) {
  const diff = myMod - theirMod;
  if (diff >= 4) return 1.30;
  if (diff >= 2) return 1.15;
  if (diff <= -4) return 0.77;
  if (diff <= -2) return 0.87;
  return 1.0;
}

export default {
  id: 'tugOfWar',
  label: 'Tug of War',
  stat: 'str',
  icon: '💪',
  description: '10 seconds of rapid taps. Higher STR multiplies each tap. Out-pull the rope.',
  paramsFromStats(myScore, theirScore) {
    return { multiplier: multiplierForMod(mod(myScore), mod(theirScore)) };
  },
  Component({ challenge, mySide, params, seed, onScore }) {
    const multiplier = params?.multiplier || 1.0;
    const startedAt  = useRef(null);
    const submitted  = useRef(false);
    const [now,    setNow]    = useState(0);
    const [taps,   setTaps]   = useState(0);
    const [done,   setDone]   = useState(false);

    useEffect(() => {
      startedAt.current = Date.now();
      let raf;
      const tick = () => {
        const elapsed = Date.now() - startedAt.current;
        if (elapsed >= GAME_DURATION_MS) {
          setNow(GAME_DURATION_MS);
          setDone(true);
          return;
        }
        setNow(elapsed);
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(raf);
    }, []);

    useEffect(() => {
      if (done && !submitted.current) {
        submitted.current = true;
        onScore(Math.round(taps * multiplier));
      }
    }, [done, taps, multiplier, onScore]);

    const tap = () => {
      if (done) return;
      setTaps((t) => t + 1);
    };

    // Rope position: -1 to +1. >0 = you winning, <0 = opponent winning.
    // Compare your effective tap rate vs the average baseline.
    const elapsedSec = Math.max(0.001, now / 1000);
    const myRate     = (taps * multiplier) / elapsedSec;
    const ropePos    = Math.max(-1, Math.min(1, (myRate - AVG_TAP_RATE_HZ) / (AVG_TAP_RATE_HZ * 1.5)));
    const remainingS = Math.max(0, (GAME_DURATION_MS - now) / 1000);

    return (
      <div style={styles.wrap}>
        <div style={styles.title}>Tug of War</div>
        <div style={styles.headerRow}>
          <span style={styles.timer}>⏳ {remainingS.toFixed(1)}s</span>
          <span style={styles.score}>Score: {Math.round(taps * multiplier)}</span>
          {multiplier !== 1 && (
            <span style={styles.mult}>×{multiplier.toFixed(2)} STR</span>
          )}
        </div>

        <div style={styles.ropeTrack}>
          <div style={styles.ropeBg} />
          <div
            style={{
              ...styles.ropeMarker,
              left: `${50 + ropePos * 45}%`,
              borderColor: ropePos > 0 ? '#5cb870' : ropePos < 0 ? '#c03333' : '#d4a847',
            }}
          >🪢</div>
          <span style={{ ...styles.endLabel, left: 4  }}>opp</span>
          <span style={{ ...styles.endLabel, right: 4 }}>you</span>
        </div>

        <button
          style={styles.tapBtn}
          onClick={tap}
          onTouchStart={(e) => { e.preventDefault(); tap(); }}
          disabled={done}
        >
          {done ? `Done — ${Math.round(taps * multiplier)}` : 'TAP TAP TAP'}
        </button>

        <div style={styles.tapHint}>{taps} taps</div>
      </div>
    );
  },
};

const styles = {
  wrap:      { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: 16 },
  title:     { fontSize: 22, fontWeight: 800, color: '#d4a847', fontFamily: "'Press Start 2P', monospace", letterSpacing: '0.06em' },
  headerRow: { display: 'flex', gap: 12, alignItems: 'center', fontSize: 14, fontWeight: 700, color: '#f0e6d0', fontFamily: "'Press Start 2P', monospace", flexWrap: 'wrap', justifyContent: 'center' },
  timer:     { background: '#0e1528', border: '1px solid #4a3d28', padding: '4px 10px', borderRadius: 4, color: '#9a8b6e' },
  score:     { background: 'rgba(212,168,71,0.15)', border: '1px solid #8b7340', padding: '4px 10px', borderRadius: 4, color: '#d4a847' },
  mult:      { background: 'rgba(192,57,43,0.15)', border: '1px solid #c03333', padding: '4px 10px', borderRadius: 4, color: '#f87171', fontSize: 11 },
  ropeTrack: { position: 'relative', width: 'min(360px, 86vw)', height: 36, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4 },
  ropeBg:    { position: 'absolute', top: '50%', left: 8, right: 8, height: 4, background: 'repeating-linear-gradient(90deg, #6a4d28 0 8px, #4a3d28 8px 16px)', transform: 'translateY(-50%)', borderRadius: 2 },
  ropeMarker:{ position: 'absolute', top: '50%', transform: 'translate(-50%, -50%)', width: 36, height: 36, border: '2px solid', borderRadius: '50%', background: '#0a1426', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, transition: 'left 60ms linear, border-color 120ms' },
  endLabel:  { position: 'absolute', top: '50%', transform: 'translateY(-50%)', fontSize: 9, color: '#9a8b6e', fontWeight: 700 },
  tapBtn:    { width: 'min(360px, 86vw)', padding: '24px 0', background: 'linear-gradient(180deg, #d4a847, #a68330)', border: '2px solid #c4a44a', color: '#1a0e00', fontSize: 22, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', borderRadius: 8, letterSpacing: '0.08em', boxShadow: '0 8px 24px rgba(212,168,71,0.4)', userSelect: 'none', WebkitUserSelect: 'none', touchAction: 'manipulation' },
  tapHint:   { fontSize: 11, color: '#9a8b6e' },
};
